import { useState, useEffect } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon, PencilIcon } from '@heroicons/react/24/outline';
import api from '../../utils/api';

const terrestrialAPI = {
  getAll:  ()        => api.get('/terrestrial-stations?active_only=false'),
  create:  (d)       => api.post('/terrestrial-stations', d),
  update:  (id, d)   => api.put(`/terrestrial-stations/${id}`, d),
  delete:  (id)      => api.delete(`/terrestrial-stations/${id}`),
};

const REGIONS = [
  'North Central','North East','North West',
  'South East','South South','South West','FCT',
];

const EMPTY = {
  name:'', frequency:'', state:'', region:'North Central', city:'',
  description:'', broadcast_day:'', broadcast_time:'', programme_name:'',
  contact_phone:'', website_url:'', is_active:1, sort_order:0,
};

export default function AdminTerrestrialPage() {
  const [stations, setStations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [filterRegion, setFilterRegion] = useState('');

  const fetch = () => {
    terrestrialAPI.getAll().then(r => setStations(r.data.data || []));
  };
  useEffect(fetch, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({ ...EMPTY, ...s });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const reset = () => { setEditing(null); setForm(EMPTY); setShowForm(false); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await terrestrialAPI.update(editing, form);
        toast.success('Station updated!');
      } else {
        await terrestrialAPI.create(form);
        toast.success('Station added!');
      }
      reset(); fetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save.');
    } finally { setSaving(false); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    await terrestrialAPI.delete(id);
    toast.success('Deleted.');
    fetch();
  };

  const filtered = filterRegion
    ? stations.filter(s => s.region === filterRegion)
    : stations;

  // Group by region
  const grouped = {};
  for (const s of filtered) {
    if (!grouped[s.region]) grouped[s.region] = [];
    grouped[s.region].push(s);
  }

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-navy-800">Terrestrial Radio Stations</h1>
          <p className="text-gray-400 text-sm mt-0.5">Local Nigerian radio stations with JNF/Tijaniyyah programming slots</p>
        </div>
        <button onClick={() => { reset(); setShowForm(true); }}
          className="btn-primary text-sm py-2 px-4 flex items-center gap-1">
          <PlusIcon className="h-4 w-4" /> Add Station
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-6 space-y-4">
          <h3 className="font-semibold text-navy-700 pb-2 border-b border-gray-100">
            {editing ? 'Edit Station' : 'Add New Station'}
          </h3>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Station Name *</label>
              <input required className="form-input" value={form.name}
                onChange={e=>set('name',e.target.value)} placeholder="e.g. Harmony FM" />
            </div>
            <div>
              <label className="form-label">Frequency *</label>
              <input required className="form-input" value={form.frequency}
                onChange={e=>set('frequency',e.target.value)} placeholder="e.g. 99.1 FM" />
            </div>
            <div>
              <label className="form-label">Programme Name</label>
              <input className="form-input" value={form.programme_name}
                onChange={e=>set('programme_name',e.target.value)} placeholder="e.g. Tijaniyyah Hour" />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Region *</label>
              <select required className="form-input" value={form.region} onChange={e=>set('region',e.target.value)}>
                {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">State *</label>
              <input required className="form-input" value={form.state}
                onChange={e=>set('state',e.target.value)} placeholder="e.g. Kwara" />
            </div>
            <div>
              <label className="form-label">City</label>
              <input className="form-input" value={form.city}
                onChange={e=>set('city',e.target.value)} placeholder="e.g. Ilorin" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Broadcast Day(s)</label>
              <input className="form-input" value={form.broadcast_day}
                onChange={e=>set('broadcast_day',e.target.value)} placeholder="e.g. Every Friday, Daily" />
            </div>
            <div>
              <label className="form-label">Broadcast Time</label>
              <input className="form-input" value={form.broadcast_time}
                onChange={e=>set('broadcast_time',e.target.value)} placeholder="e.g. 06:00 - 07:00" />
            </div>
          </div>

          <div>
            <label className="form-label">Description</label>
            <textarea rows={2} className="form-input resize-none" value={form.description}
              onChange={e=>set('description',e.target.value)} placeholder="Brief description of the programme..." />
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Contact Phone</label>
              <input className="form-input" value={form.contact_phone}
                onChange={e=>set('contact_phone',e.target.value)} />
            </div>
            <div>
              <label className="form-label">Website URL</label>
              <input className="form-input" value={form.website_url}
                onChange={e=>set('website_url',e.target.value)} placeholder="https://..." />
            </div>
            <div className="flex items-center gap-3 mt-5">
              <input type="checkbox" id="active" checked={!!form.is_active}
                onChange={e=>set('is_active',e.target.checked?1:0)} className="h-4 w-4 accent-cyan-500" />
              <label htmlFor="active" className="text-sm text-navy-700 font-medium">Active / Visible</label>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={saving} className="btn-primary text-sm py-2.5 px-6 flex items-center gap-2">
              {saving ? <><div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Saving...</> : (editing ? 'Update Station' : 'Add Station')}
            </button>
            <button type="button" onClick={reset} className="btn-outline text-sm py-2.5 px-6">Cancel</button>
          </div>
        </form>
      )}

      {/* Region filter */}
      <div className="flex gap-2 mb-5 flex-wrap">
        <button onClick={()=>setFilterRegion('')}
          className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${!filterRegion?'bg-navy-700 text-white border-navy-700':'bg-white border-gray-200 text-gray-600'}`}>
          All Regions ({stations.length})
        </button>
        {REGIONS.filter(r=>stations.some(s=>s.region===r)).map(r=>(
          <button key={r} onClick={()=>setFilterRegion(r)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${filterRegion===r?'bg-navy-700 text-white border-navy-700':'bg-white border-gray-200 text-gray-600'}`}>
            {r} ({stations.filter(s=>s.region===r).length})
          </button>
        ))}
      </div>

      {/* Stations grouped */}
      {Object.entries(grouped).map(([region, list]) => (
        <div key={region} className="mb-6">
          <h2 className="text-sm font-bold text-navy-600 uppercase tracking-wider mb-3 flex items-center gap-2">
            <div className="h-px flex-1 bg-gray-100" />
            📡 {region}
            <div className="h-px flex-1 bg-gray-100" />
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {list.map(s => (
              <div key={s.id} className={`bg-white rounded-2xl border shadow-sm p-4 ${s.is_active?'border-gray-100':'border-dashed border-gray-200 opacity-60'}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-bold text-navy-800">{s.name}</p>
                    <span className="inline-block bg-cyan-100 text-cyan-700 text-xs font-bold px-2 py-0.5 rounded-full mt-0.5">{s.frequency}</span>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={()=>startEdit(s)} className="p-1.5 text-gray-400 hover:text-navy-600 hover:bg-navy-50 rounded-lg"><PencilIcon className="h-3.5 w-3.5"/></button>
                    <button onClick={()=>del(s.id,s.name)} className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><TrashIcon className="h-3.5 w-3.5"/></button>
                  </div>
                </div>
                {s.programme_name && <p className="text-navy-600 text-xs font-medium mb-1">📻 {s.programme_name}</p>}
                {s.city && <p className="text-gray-400 text-xs">📍 {s.city}, {s.state}</p>}
                {(s.broadcast_day || s.broadcast_time) && (
                  <p className="text-gray-500 text-xs mt-1">
                    🕐 {[s.broadcast_day, s.broadcast_time].filter(Boolean).join(' · ')}
                  </p>
                )}
                {!s.is_active && <span className="text-xs text-gray-400 italic">Hidden</span>}
              </div>
            ))}
          </div>
        </div>
      ))}

      {stations.length === 0 && (
        <p className="text-center text-gray-400 py-12">No stations yet. Add your first one above.</p>
      )}
    </AdminLayout>
  );
}
